/**
 * API Client for CADAM Backend
 * Replaces Supabase client with simple REST API calls
 */

const API_BASE = '/api';

export interface User {
  id: string;
  name: string;
}

class APIClient {
  private token: string | null = null;

  constructor() {
    this.token = localStorage.getItem('cadam_token');
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('cadam_token', token);
  }

  getToken(): string | null {
    return this.token;
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('cadam_token');
  }

  async fetch<T>(url: string, options?: RequestInit): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...((options?.headers as Record<string, string>) || {}),
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE}${url}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || `Request failed: ${response.status}`);
    }

    return response.json();
  }

  async *stream(url: string, body: object): AsyncGenerator<any> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE}${url}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`Request failed: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No reader available');
    }

    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (trimmed) {
            try {
              yield JSON.parse(trimmed);
            } catch {
              // Skip invalid JSON
            }
          }
        }
      }

      // Process remaining buffer
      const final = decoder.decode();
      if (final) buffer += final;
      const tail = buffer.trim();
      if (tail) {
        try {
          yield JSON.parse(tail);
        } catch {
          // Skip invalid JSON
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  // Health check
  async health(): Promise<{ status: string }> {
    return this.fetch('/health');
  }

  // Auth validation - actually checks the token
  async validateToken(): Promise<{ valid: boolean; user: { id: string; name: string } }> {
    return this.fetch('/auth/validate');
  }

  // Conversations
  async getConversations(): Promise<any[]> {
    return this.fetch('/conversations');
  }

  async getConversation(id: string): Promise<any> {
    return this.fetch(`/conversations/${id}`);
  }

  async createConversation(title?: string, id?: string): Promise<any> {
    return this.fetch('/conversations', {
      method: 'POST',
      body: JSON.stringify({ title, id }),
    });
  }

  async updateConversation(id: string, data: any): Promise<any> {
    return this.fetch(`/conversations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteConversation(id: string): Promise<void> {
    return this.fetch(`/conversations/${id}`, { method: 'DELETE' });
  }

  // Messages
  async getMessages(conversationId: string): Promise<any[]> {
    return this.fetch(`/conversations/${conversationId}/messages`);
  }

  async createMessage(data: {
    conversation_id: string;
    role: string;
    content: any;
    parent_message_id?: string | null;
  }): Promise<any> {
    return this.fetch('/messages', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateMessage(id: string, content: any): Promise<any> {
    return this.fetch(`/messages/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ content }),
    });
  }

  // Chat (streaming)
  chat(data: {
    messageId: string;
    conversationId: string;
    model?: string;
    newMessageId?: string;
    thinking?: boolean;
  }): AsyncGenerator<any> {
    return this.stream('/chat', data);
  }

  // Prompt generation
  async generatePrompt(existingText?: string): Promise<{ prompt: string }> {
    return this.fetch('/prompt', {
      method: 'POST',
      body: JSON.stringify({ existingText }),
    });
  }

  // Title generation
  async generateTitle(
    conversationId: string,
    content: any
  ): Promise<{ title: string }> {
    return this.fetch('/title', {
      method: 'POST',
      body: JSON.stringify({ conversationId, content }),
    });
  }

  // Image upload
  async uploadImage(file: File, conversationId?: string): Promise<{ id: string; url: string }> {
    const formData = new FormData();
    formData.append('file', file);
    if (conversationId) {
      formData.append('conversation_id', conversationId);
    }

    const headers: Record<string, string> = {};
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE}/images/upload`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status}`);
    }

    return response.json();
  }

  // Image download - returns blob URL
  async getImage(userId: string, conversationId: string, imageId: string): Promise<string> {
    const headers: Record<string, string> = {};
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(
      `${API_BASE}/images/${userId}/${conversationId}/${imageId}`,
      { headers }
    );

    if (!response.ok) {
      throw new Error(`Image download failed: ${response.status}`);
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }

  // Get conversations with details (for history view)
  async getConversationsWithDetails(): Promise<any[]> {
    return this.fetch('/conversations?details=true');
  }

  // Get recent conversations (for sidebar)
  async getRecentConversations(limit: number = 10): Promise<any[]> {
    return this.fetch(`/conversations?limit=${limit}`);
  }

  // CAD Conversion
  async getCADConversionStatus(): Promise<{
    available: boolean;
    converter: string | null;
    supported_formats: string[];
  }> {
    return this.fetch('/cad/status');
  }

  async convertCADToSTL(
    file: File,
    conversationId?: string
  ): Promise<{
    success: boolean;
    stl_id?: string;
    stl_url?: string;
    error?: string;
    bounding_box?: { x: number; y: number; z: number };
  }> {
    const formData = new FormData();
    formData.append('file', file);
    if (conversationId) {
      formData.append('conversation_id', conversationId);
    }

    const headers: Record<string, string> = {};
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE}/cad/convert`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`CAD conversion failed: ${response.status}`);
    }

    return response.json();
  }
}

export const api = new APIClient();
